<template>
  <div class="tiptap-editor-container">
    <div class="menu-bar">
      <!-- Text formatting -->
      <div class="menu-group">
        <button data-action="bold" title="Bold"><i class="fas fa-bold"></i></button>
        <button data-action="italic" title="Italic"><i class="fas fa-italic"></i></button>
        <button data-action="strike" title="Strikethrough"><i class="fas fa-strikethrough"></i></button>
      </div>

      <!-- Text styling -->
      <div class="menu-group">
        <button data-action="link" title="Add Link"><i class="fas fa-link"></i></button>
        <button data-action="image" title="Insert Image"><i class="fas fa-image"></i></button>
      </div>

      <!-- Headings dropdown -->
      <div class="menu-group">
        <div class="dropdown">
          <button class="dropdown-toggle" title="Headings"><i class="fas fa-heading"></i> <i class="fas fa-caret-down"></i></button>
          <div class="dropdown-menu">
            <button data-action="heading" data-level="1" title="Heading 1">H1</button>
            <button data-action="heading" data-level="2" title="Heading 2">H2</button>
            <button data-action="heading" data-level="3" title="Heading 3">H3</button>
          </div>
        </div>
      </div>

      <!-- Lists -->
      <div class="menu-group">
        <button data-action="bulletList" title="Bullet List"><i class="fas fa-list-ul"></i></button>
        <button data-action="orderedList" title="Ordered List"><i class="fas fa-list-ol"></i></button>
        <button data-action="taskList" title="Task List"><i class="fas fa-tasks"></i></button>
      </div>

      <!-- Block elements -->
      <div class="menu-group">
        <button data-action="blockquote" title="Quote"><i class="fas fa-quote-right"></i></button>
        <button data-action="codeBlock" title="Code Block"><i class="fas fa-code"></i></button>
        <button data-action="horizontalRule" title="Horizontal Rule"><i class="fas fa-minus"></i></button>
      </div>

      <!-- Table dropdown -->
      <div class="menu-group">
        <div class="dropdown">
          <button class="dropdown-toggle" title="Table"><i class="fas fa-table"></i> <i class="fas fa-caret-down"></i></button>
          <div class="dropdown-menu">
            <button data-action="insertTable" title="Insert Table"><i class="fas fa-table"></i> Insert</button>
            <button data-action="addColumnBefore" title="Add Column Before"><i class="fas fa-plus"></i> Column Before</button>
            <button data-action="addColumnAfter" title="Add Column After"><i class="fas fa-plus"></i> Column After</button>
            <button data-action="addRowBefore" title="Add Row Before"><i class="fas fa-plus"></i> Row Before</button>
            <button data-action="addRowAfter" title="Add Row After"><i class="fas fa-plus"></i> Row After</button>
            <button data-action="deleteColumn" title="Delete Column"><i class="fas fa-trash-alt"></i> Column</button>
            <button data-action="deleteRow" title="Delete Row"><i class="fas fa-trash-alt"></i> Row</button>
            <button data-action="deleteTable" title="Delete Table"><i class="fas fa-trash-alt"></i> Table</button>
          </div>
        </div>
      </div>

      <!-- Text style dropdown -->
      <div class="menu-group">
          <div class="dropdown">
              <button class="dropdown-toggle" title="Text Style"><i class="fas fa-font"></i> <i class="fas fa-caret-down"></i></button>
              <div class="dropdown-menu">
                  <button data-action="subscript" title="Subscript"><i class="fas fa-subscript"></i> Subscript</button>
                  <button data-action="superscript" title="Superscript"><i class="fas fa-superscript"></i> Superscript</button>
              </div>
          </div>
      </div>

      <!-- Export -->
      <div class="menu-group">
        <div class="dropdown">
          <button class="dropdown-toggle" title="Import/Export"><i class="fas fa-file-alt"></i> <i class="fas fa-caret-down"></i></button>
          <div class="dropdown-menu">
            <button data-action="importMarkdown" title="Import from Markdown"><i class="fas fa-file-import"></i> Import Markdown</button>
            <button data-action="exportMarkdown" title="Export as Markdown"><i class="fas fa-file-export"></i> Export Markdown</button>
          </div>
        </div>
      </div>

      <!-- History -->
      <div class="menu-group">
        <button data-action="undo" title="Undo"><i class="fas fa-undo"></i></button>
        <button data-action="redo" title="Redo"><i class="fas fa-redo"></i></button>
      </div>
    </div>
    
    <div class="editor" ref="editor"></div>

    <!-- Hidden file inputs -->
    <div style="display: none;">
      <label for="image-upload" id="image-upload-label">
        <input type="file" id="image-upload" accept="image/*" />
      </label>
      <label for="markdown-import" id="markdown-import-label">
        <input type="file" id="markdown-import" accept=".md,.markdown,text/markdown" />
      </label>
    </div>

    <!-- Modal for displaying markdown -->
    <div id="markdown-modal" class="modal">
      <div class="modal-content">
        <span class="close">&times;</span>
        <h2>Markdown Export</h2>
        <div class="modal-buttons">
          <button id="copy-markdown">Copy to Clipboard</button>
          <button id="download-markdown">Download as .md</button>
        </div>
        <pre id="markdown-output"></pre>
      </div>
    </div>

    <!-- Notification container -->
    <div id="tiptap-notification" class="tiptap-notification"></div>
  </div>
</template>

<script>
import { Editor, generateJSON } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Strike from '@tiptap/extension-strike'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import TextStyle from '@tiptap/extension-text-style'
import Link from '@tiptap/extension-link'
import Heading from '@tiptap/extension-heading'
import Image from '@tiptap/extension-image'
import MarkdownIt from 'markdown-it'
import TurndownService from 'turndown'
import markdownItTaskLists from 'markdown-it-task-lists'
import markdownItSup from 'markdown-it-sup'
import markdownItSub from 'markdown-it-sub'

export default {
  name: 'TiptapEditor',
  props: {
    content: {
      type: String,
      default: ''
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    // Define extensions array for reuse
    const extensions = [
      StarterKit.configure({
        codeBlock: true,
        horizontalRule: true,
        hardBreak: true,
        strike: false,
        heading: false
      }),
      Heading.configure({
        levels: [1, 2, 3],
        HTMLAttributes: {
          class: 'editor-heading'
        }
      }),
      Image.configure({
        allowBase64: true,
        HTMLAttributes: {
          class: 'editor-image'
        }
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'editor-table'
        },
        defaultOptions: {
          withHeaderRow: true
        }
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: 'editor-table-row'
        }
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'editor-table-header'
        }
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'editor-table-cell'
        }
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Strike.configure({
        HTMLAttributes: {
          class: 'editor-strike'
        }
      }),
      Subscript.configure({
        HTMLAttributes: {
          class: 'editor-subscript',
          style: 'vertical-align: sub; font-size: 0.8em;'
        },
        inclusive: true,
        parseHTML: () => [
          { tag: 'sub' },
          { style: 'vertical-align=sub' }
        ],
        renderHTML: ({ HTMLAttributes }) => ['sub', HTMLAttributes, 0]
      }),
      Superscript.configure({
        HTMLAttributes: {
          class: 'editor-superscript',
          style: 'vertical-align: super; font-size: 0.8em;'
        },
        inclusive: true,
        parseHTML: () => [
          { tag: 'sup' },
          { style: 'vertical-align=super' }
        ],
        renderHTML: ({ HTMLAttributes }) => ['sup', HTMLAttributes, 0]
      }),
      TextStyle.configure({
        types: ['textStyle']
      }),
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          class: 'custom-link',
        },
      }),
    ]

    // Custom markdown-it instance with superscript and subscript support
    const md = new MarkdownIt({
      html: true,
      xhtmlOut: true,
      breaks: true,
      linkify: true,
      typographer: true
    })

    // Add superscript and subscript support
    md.use(markdownItSup)
    md.use(markdownItSub)

    // Use task lists plugin with custom renderer
    md.use(markdownItTaskLists, {
      enabled: true,
      label: false,         // ✅ This disables the extra <label> with raw Markdown
      labelAfter: false,    // ✅ Also disable label injection after checkbox
      ulClass: 'contains-task-list',
      liClass: 'task-list-item',
      checkboxClass: 'task-list-item-checkbox',
    })

    return {
      editor: null,
      extensions,
      isUpdating: false,
      md,
      notificationTimeout: null,
      lastHeadingActionTime: 0,
      isTableInsertInProgress: false,
      isTableOperationInProgress: false,
      markdownModal: null,
      markdownOutput: null,
      closeModalBtn: null,
      copyMarkdownBtn: null,
      downloadMarkdownBtn: null,
      isImporting: false,
      turndownService: new TurndownService({
        headingStyle: 'atx',
        codeBlockStyle: 'fenced',
        emDelimiter: '*'
      })
    }
  },
  mounted() {
    this.setupTurndownRules()
    this.initializeEditor()
    this.setupEventListeners()
    this.setupModalElements()
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy()
    }
    window.removeEventListener('keydown', this.keyEvent)
  },
  watch: {
    content: {
      handler(newContent) {
        if (this.isUpdating || !this.editor) return
        
        try {
          this.isUpdating = true
          
          const currentHtml = this.editor.getHTML()
          if (newContent === currentHtml) return
          let html = this.md.render(newContent)

          html = this.transformTaskListHtml(html)

          const json = generateJSON(html, this.extensions)

          this.editor.commands.setContent(json)
        } catch (error) {
          console.error('Error updating editor content:', error)
        } finally {
          this.isUpdating = false
        }
      },
      immediate: true
    }
  },
  methods: {
    setupTurndownRules() {
      this.turndownService.addRule('headings', {
        filter: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
        replacement: function(content, node) {
          const level = parseInt(node.nodeName.charAt(1))
          return '\n' + '#'.repeat(level) + ' ' + content + '\n\n'
        }
      })

      this.turndownService.addRule('strikethrough', {
        filter: ['s', 'del', 'strike'],
        replacement: function(content) {
          return '~~' + content + '~~'
        }
      })

      this.turndownService.addRule('table', {
        filter: 'table',
        replacement: function(content, node) {
          const allRows = Array.from(node.querySelectorAll('tr'))
          
          const headerRow = Array.from(node.querySelectorAll('thead th, tr:first-child th, tr:first-child td'))
            .map(cell => cell.textContent.trim() || ' ')
          
          const hasHeaders = headerRow.length > 0
          const effectiveHeaderRow = hasHeaders ? headerRow : allRows[0]?.querySelectorAll('td')
            .map(td => td.textContent.trim() || ' ') || Array(3).fill(' ')
          
          let markdown = '| ' + effectiveHeaderRow.join(' | ') + ' |\n'
          markdown += '| ' + effectiveHeaderRow.map(() => '---').join(' | ') + ' |\n'
          
          const bodyRows = hasHeaders ? allRows.slice(1) : allRows
          
          
          bodyRows.forEach(row => {
            const cells = Array.from(row.querySelectorAll('td'))
              .map(td => td.textContent.trim() || ' ')
            while (cells.length < effectiveHeaderRow.length) cells.push(' ')
            markdown += '| ' + cells.join(' | ') + ' |\n'
          })
          
          return '\n' + markdown + '\n'
        }
      })

      this.turndownService.addRule('taskListItems', {
        filter: function(node) {
          return node.nodeName === 'LI' && 
                 node.parentNode && 
                 node.parentNode.nodeName === 'UL' && 
                 node.parentNode.getAttribute('data-type') === 'taskList';
        },
        replacement: function(content, node) {
          const checkbox = node.querySelector('input[type="checkbox"]');
          const isChecked = checkbox && checkbox.checked;
          const taskMark = isChecked ? '[x]' : '[ ]';
          
          content = content.replace(/^\s*\[\s?[xX]?\s?\]\s*/, '').trim();
          
          return `- ${taskMark} ${content}`;
        }
      });

      this.turndownService.addRule('codeBlocks', {
        filter: function(node) {
          return node.nodeName === 'PRE' && node.firstChild?.nodeName === 'CODE'
        },
        replacement: function(content, node) {
          const code = node.firstChild.textContent
          const language = node.firstChild.className.replace('language-', '')
          return '\n```' + language + '\n' + code + '\n```\n'
        }
      })

      this.turndownService.addRule('links', {
        filter: function(node) {
          return node.nodeName === 'A' && node.getAttribute('href')
        },
        replacement: function(content, node) {
          const href = node.getAttribute('href')
          const title = node.title ? ` "${node.title}"` : ''
          return `[${content}](${href}${title})`
        }
      })

      this.turndownService.addRule('images', {
        filter: 'img',
        replacement: function(content, node) {
          const alt = node.getAttribute('alt') || ''
          const src = node.getAttribute('src') || ''
          const title = node.getAttribute('title') ? ` "${node.getAttribute('title')}"` : ''
          return `![${alt}](${src}${title})`
        }
      })

      // Update subscript rule to handle both HTML and style-based subscripts
      this.turndownService.addRule('subscript', {
        filter: function(node) {
          return (
            node.nodeName === 'SUB' ||
            (node.nodeName === 'SPAN' && 
             (node.classList.contains('editor-subscript') || 
              node.style.verticalAlign === 'sub'))
          )
        },
        replacement: function(content) {
          return `~${content}~`
        }
      })

      // Update superscript rule to handle both HTML and style-based superscripts
      this.turndownService.addRule('superscript', {
        filter: function(node) {
          return (
            node.nodeName === 'SUP' ||
            (node.nodeName === 'SPAN' && 
             (node.classList.contains('editor-superscript') || 
              node.style.verticalAlign === 'super'))
          )
        },
        replacement: function(content) {
          return `^${content}^`
        }
      })
    },

    initializeEditor() {
      try {
        let html = this.md.render(this.content)
        html = this.transformTaskListHtml(html)
        const initialJson = generateJSON(html, this.extensions)

        this.editor = new Editor({
          element: this.$refs.editor,
          extensions: this.extensions,
          content: initialJson,
          editable: !this.readOnly,
          onUpdate: ({ editor }) => {
            if (this.isUpdating) return
            
            try {
              this.isUpdating = true
              const markdown = this.turndownService.turndown(editor.getHTML())
              this.$emit('update', markdown)
            } catch (error) {
              console.error('Error in editor update:', error)
            } finally {
              this.isUpdating = false
            }
          },
          onTransaction: () => {
            this.updateMenuState()
          }
        })

        if (!document.getElementById('tiptap-notification')) {
          const notificationContainer = document.createElement('div')
          notificationContainer.id = 'tiptap-notification'
          notificationContainer.style.display = 'none'
          document.body.appendChild(notificationContainer)
        }
      } catch (error) {
        console.error('Error initializing editor:', error)
      }
    },

    setupEventListeners() {
      this.$el.querySelectorAll('.menu-bar button:not(.dropdown-toggle)').forEach(button => {
        button.addEventListener('click', () => {
          const action = button.dataset.action
          if (action) {
            this.executeAction(action, button)
          }
        })
      })

      this.$el.querySelectorAll('.dropdown-menu button').forEach(button => {
        button.addEventListener('click', (e) => {
          e.stopPropagation()
          const action = button.dataset.action
          if (action) {
            this.executeAction(action, button)
          }
        })
      })

      this.setupDropdowns()

      window.addEventListener('keydown', this.keyEvent)
    },

    keyEvent(event) {
      if (!event.ctrlKey && !event.metaKey) return

      switch (event.key.toLowerCase()) {
        case 'b':
          event.preventDefault()
          this.editor.chain().focus().toggleBold().run()
          break
        case 'i':
          event.preventDefault()
          this.editor.chain().focus().toggleItalic().run()
          break
        case 'z':
          event.preventDefault()
          if (event.shiftKey) {
            this.editor.chain().focus().redo().run()
          } else {
            this.editor.chain().focus().undo().run()
          }
          break
      }
    },

    executeAction(action, button) {
      if (this.readOnly) return

      if (this.isTableOperationInProgress) {
        return
      }

      // Prevent double execution of import action
      if (action === 'importMarkdown' && this.isImporting) {
        return
      }

      // Ensure editor has focus before executing any action
      this.editor.commands.focus()

      switch (action) {
        case 'bold':
          this.editor.chain().focus().toggleBold().run()
          break
        case 'italic':
          this.editor.chain().focus().toggleItalic().run()
          break
        case 'strike':
          this.editor.chain().focus().toggleStrike().run()
          break
        case 'link': {
          const url = prompt('Enter URL:')
          if (url) {
            if (this.editor.view.state.selection.empty) {
              const text = prompt('Enter link text:')
              if (text) {
                this.editor.chain()
                  .focus()
                  .insertContent(`<a href="${url}" target="_blank">${text}</a>`)
                  .run()
              }
            } else {
              this.editor.chain()
                .focus()
                .setLink({ href: url, target: '_blank' })
                .run()
            }
          }
          break
        }
        case 'heading': {
          const level = parseInt(button.dataset.level || '1')
          const now = Date.now()
          
          if (now - this.lastHeadingActionTime < 300) {
            return
          }
          this.lastHeadingActionTime = now

          if (this.editor.isActive('heading', { level })) {
            this.editor.chain().focus().setParagraph().run()
          } else {
            this.editor.chain().focus().clearNodes().setHeading({ level }).run()
          }
          break
        }
        case 'bulletList':
          this.editor.chain().focus().toggleBulletList().run()
          break
        case 'orderedList':
          this.editor.chain().focus().toggleOrderedList().run()
          break
        case 'taskList':
          this.editor.chain().focus().toggleTaskList().run()
          break
        case 'blockquote':
          this.editor.chain().focus().toggleBlockquote().run()
          break
        case 'codeBlock':
          this.editor.chain().focus().toggleCodeBlock().run()
          break
        case 'horizontalRule':
          this.editor.chain().focus().setHorizontalRule().run()
          break
        case 'undo':
          this.editor.chain().focus().undo().run()
          break
        case 'redo':
          this.editor.chain().focus().redo().run()
          break
        case 'image': {
          const input = document.getElementById('image-upload')
          if (input) {
            input.click()
            input.onchange = (event) => {
              const file = event.target.files[0]
              if (file) {
                const reader = new FileReader()
                reader.onload = (e) => {
                  const altText = prompt('Enter alt text for the image (for accessibility):', '')
                  this.editor.chain()
                    .focus()
                    .setImage({ 
                      src: e.target.result,
                      alt: altText || '',
                      title: file.name
                    })
                    .run()
                }
                reader.readAsDataURL(file)
              }
            }
          }
          break
        }
        case 'insertTable':
          if (this.isTableInsertInProgress) return
          this.isTableInsertInProgress = true
          try {
            this.editor.chain()
              .focus()
              .insertTable({
                rows: 3,
                cols: 3,
                withHeaderRow: true
              })
              .run()

            this.$nextTick(() => {
              const table = this.editor.view.dom.querySelector('table')
              if (table) {
                const allRows = table.querySelectorAll('tr')
                const headerRows = table.querySelectorAll('thead tr')
                const bodyRows = table.querySelectorAll('tbody tr')
              }
            })
          } finally {
            setTimeout(() => {
              this.isTableInsertInProgress = false
            }, 300)
          }
          break
        case 'addColumnBefore':
          this.isTableOperationInProgress = true
          this.editor.chain().focus().addColumnBefore().run()
          setTimeout(() => { this.isTableOperationInProgress = false }, 100)
          break
        case 'addColumnAfter':
          this.isTableOperationInProgress = true
          this.editor.chain().focus().addColumnAfter().run()
          setTimeout(() => { this.isTableOperationInProgress = false }, 100)
          break
        case 'addRowBefore':
          this.isTableOperationInProgress = true
          this.editor.chain().focus().addRowBefore().run()
          setTimeout(() => { this.isTableOperationInProgress = false }, 100)
          break
        case 'addRowAfter':
          this.isTableOperationInProgress = true
          this.editor.chain().focus().addRowAfter().run()
          setTimeout(() => { this.isTableOperationInProgress = false }, 100)
          break
        case 'deleteColumn':
          this.isTableOperationInProgress = true
          this.editor.chain().focus().deleteColumn().run()
          setTimeout(() => { this.isTableOperationInProgress = false }, 100)
          break
        case 'deleteRow':
          this.isTableOperationInProgress = true
          this.editor.chain().focus().deleteRow().run()
          setTimeout(() => { this.isTableOperationInProgress = false }, 100)
          break
        case 'deleteTable':
          this.isTableOperationInProgress = true
          this.editor.chain().focus().deleteTable().run()
          setTimeout(() => { this.isTableOperationInProgress = false }, 100)
          break
        case 'exportMarkdown':
          this.showMarkdownExport()
          break
        case 'importMarkdown':
          this.isImporting = true
          this.safelyOpenFileDialog('.md,.markdown,text/markdown', (file) => {
            if (file) {
              const reader = new FileReader()
              reader.onload = (e) => {
                try {
                  this.importMarkdown(e.target.result)
                  this.showNotification(`File "${file.name}" imported successfully`, 'success')
                } catch (error) {
                  console.error('Error importing markdown:', error)
                  this.showNotification('Error importing markdown file', 'error')
                } finally {
                  this.isImporting = false
                }
              }
              reader.onerror = () => {
                console.error('Error reading the markdown file')
                this.showNotification('Error reading the file', 'error')
                this.isImporting = false
              }
              reader.readAsText(file)
            } else {
              this.isImporting = false
            }
          })
          break
        case 'subscript': {
          // Check if there's a text selection
          const { from, to } = this.editor.state.selection
          if (from !== to) {
            // If superscript is active, remove it first
            if (this.editor.isActive('superscript')) {
              this.editor.chain().focus().unsetSuperscript().run()
            }
            // Apply subscript to selected text
            this.editor.chain()
              .focus()
              .setSubscript()
              .run()
          } else {
            // Toggle subscript if no selection
            this.editor.chain()
              .focus()
              .toggleSubscript()
              .run()
          }
          // Close dropdown after action
          if (button.closest('.dropdown-menu')) {
            this.closeAllDropdowns()
          }
          break
        }
        case 'superscript': {
          // Check if there's a text selection
          const { from, to } = this.editor.state.selection
          if (from !== to) {
            // If subscript is active, remove it first
            if (this.editor.isActive('subscript')) {
              this.editor.chain().focus().unsetSubscript().run()
            }
            // Apply superscript to selected text
            this.editor.chain()
              .focus()
              .setSuperscript()
              .run()
          } else {
            // Toggle superscript if no selection
            this.editor.chain()
              .focus()
              .toggleSuperscript()
              .run()
          }
          // Close dropdown after action
          if (button.closest('.dropdown-menu')) {
            this.closeAllDropdowns()
          }
          break
        }
      }
    },

    setupDropdowns() {
      this.$el.querySelectorAll('.dropdown-toggle').forEach(toggle => {
        toggle.setAttribute('aria-expanded', 'false')
        
        toggle.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          
          const dropdown = toggle.closest('.dropdown')
          const menu = dropdown.querySelector('.dropdown-menu')
          
          this.$el.querySelectorAll('.dropdown-toggle[aria-expanded="true"]').forEach(openToggle => {
            if (openToggle !== toggle) {
              openToggle.setAttribute('aria-expanded', 'false')
              const openMenu = openToggle.parentElement.querySelector('.dropdown-menu')
              openMenu.style.display = ''
            }
          })
          
          const isExpanded = toggle.getAttribute('aria-expanded') === 'true'
          toggle.setAttribute('aria-expanded', isExpanded ? 'false' : 'true')
          
          menu.style.display = isExpanded ? '' : 'block'
        })
      })
      
      document.addEventListener('click', () => {
        this.$el.querySelectorAll('.dropdown-toggle[aria-expanded="true"]').forEach(toggle => {
          toggle.setAttribute('aria-expanded', 'false')
          const menu = toggle.parentElement.querySelector('.dropdown-menu')
          menu.style.display = ''
        })
      })
    },

    updateMenuState() {
      if (!this.editor) return

      const headingButtons = this.$el.querySelectorAll('button[data-action="heading"]')
      const headingToggle = this.$el.querySelector('.dropdown-toggle[title="Headings"]')
      
      let isAnyHeadingActive = false
      
      headingButtons.forEach(button => {
        const level = parseInt(button.dataset.level || '1')
        const isActive = this.editor.isActive('heading', { level })
        button.classList.toggle('is-active', isActive)
        
        if (isActive) {
          isAnyHeadingActive = true
          if (headingToggle) {
            headingToggle.innerHTML = `<i class="fas fa-heading"></i> H${level} <i class="fas fa-caret-down"></i>`
          }
        }
      })

      if (headingToggle && !isAnyHeadingActive) {
        headingToggle.innerHTML = `<i class="fas fa-heading"></i> <i class="fas fa-caret-down"></i>`
      }
      headingToggle?.classList.toggle('is-active', isAnyHeadingActive)

      const formatButtons = {
        bold: 'bold',
        italic: 'italic',
        strike: 'strike',
        link: 'link',
        subscript: 'subscript',
        superscript: 'superscript'
      }

      Object.entries(formatButtons).forEach(([action, command]) => {
        this.$el.querySelectorAll(`button[data-action="${action}"]`).forEach(button => {
          button.classList.toggle('is-active', this.editor.isActive(command))
        })
      })

      // Update text style dropdown state with more precise checks
      const textStyleToggle = this.$el.querySelector('.dropdown-toggle[title="Text Style"]')
      if (textStyleToggle) {
        const isSubscriptActive = this.editor.isActive('subscript')
        const isSuperscriptActive = this.editor.isActive('superscript')
        
        // Update dropdown toggle state
        textStyleToggle.classList.toggle('is-active', isSubscriptActive || isSuperscriptActive)
        
        // Update individual button states in dropdown with more precise checks
        const subscriptButton = this.$el.querySelector('button[data-action="subscript"]')
        const superscriptButton = this.$el.querySelector('button[data-action="superscript"]')
        
        if (subscriptButton) {
          subscriptButton.classList.toggle('is-active', isSubscriptActive)
          // Add title attribute to show current state
          subscriptButton.title = isSubscriptActive ? 'Remove Subscript' : 'Apply Subscript'
        }
        if (superscriptButton) {
          superscriptButton.classList.toggle('is-active', isSuperscriptActive)
          // Add title attribute to show current state
          superscriptButton.title = isSuperscriptActive ? 'Remove Superscript' : 'Apply Superscript'
        }
      }
    },

    showNotification(message, type = 'info') {
      const notification = document.getElementById('tiptap-notification')
      
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout)
      }
      
      notification.textContent = message
      notification.className = ''
      notification.classList.add('tiptap-notification', `notification-${type}`)
      notification.style.display = 'block'
      
      this.notificationTimeout = setTimeout(() => {
        notification.style.display = 'none'
      }, 3000)
    },

    setupModalElements() {
      this.markdownModal = document.getElementById('markdown-modal')
      this.markdownOutput = document.getElementById('markdown-output')
      this.closeModalBtn = document.querySelector('.close')
      this.copyMarkdownBtn = document.getElementById('copy-markdown')
      this.downloadMarkdownBtn = document.getElementById('download-markdown')

      if (this.closeModalBtn) {
        this.closeModalBtn.addEventListener('click', () => {
          this.markdownModal.style.display = 'none'
        })
      }

      if (this.copyMarkdownBtn) {
        this.copyMarkdownBtn.addEventListener('click', () => {
          if (this.markdownOutput) {
            navigator.clipboard.writeText(this.markdownOutput.textContent)
              .then(() => {
                this.showNotification('Markdown copied to clipboard!', 'success')
              })
              .catch(err => {
                console.error('Failed to copy: ', err)
                this.showNotification('Failed to copy to clipboard', 'error')
              })
          }
        })
      }

      if (this.downloadMarkdownBtn) {
        this.downloadMarkdownBtn.addEventListener('click', () => {
          if (this.markdownOutput) {
            const markdown = this.markdownOutput.textContent
            const blob = new Blob([markdown], { type: 'text/markdown' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'document.md'
            a.click()
            URL.revokeObjectURL(url)
          }
        })
      }
    },

    showMarkdownExport() {
      // Close any open dropdowns first
      this.closeAllDropdowns()
      
      // Convert editor content to markdown
      const markdown = this.turndownService.turndown(this.editor.getHTML())
      
      if (this.markdownOutput) {
        this.markdownOutput.textContent = markdown
        this.markdownModal.style.display = 'block'
      }
    },

    importMarkdown(markdown) {
      try {
        // Initialize markdown-it parser with task list support
        const md = new MarkdownIt({
          html: true,
          breaks: true,
          linkify: true,
          typographer: true
        }).use(markdownItTaskLists, {
          enabled: true,
          label: false,         // ✅ This disables the extra <label> with raw Markdown
          labelAfter: false,    // ✅ Also disable label injection after checkbox
          ulClass: 'contains-task-list',
          liClass: 'task-list-item',
          checkboxClass: 'task-list-item-checkbox',
        })

        // Convert markdown to HTML
        let html = md.render(markdown)

        // Transform task list HTML
        html = this.transformTaskListHtml(html)

        // Convert HTML to Tiptap JSON
        const json = generateJSON(html, this.extensions)

        // Set the content
        this.editor.commands.setContent(json)
      } catch (error) {
        console.error('Error importing markdown:', error)
        throw new Error('Failed to import markdown')
      }
    },

    safelyOpenFileDialog(accept, callback) {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = accept
      input.style.display = 'none'
      
      const handleChange = (event) => {
        const file = event.target.files[0]
        if (file && callback) {
          callback(file)
        }
        // Clean up
        input.removeEventListener('change', handleChange)
        if (document.body.contains(input)) {
          document.body.removeChild(input)
        }
      }

      input.addEventListener('change', handleChange, { once: true })
      document.body.appendChild(input)
      input.click()
    },

    transformTaskListHtml(html) {
      // If the HTML already has the correct structure, return it as is
      if (html.includes('data-type="taskList"') && html.includes('data-type="taskItem"')) {
        return html
      }

      const div = document.createElement('div')
      div.innerHTML = html

      // Transform superscript and subscript formatting
      this.transformSuperscriptSubscript(div)

      // Find lists that contain checkboxes
      const lists = div.querySelectorAll('ul')
      lists.forEach(list => {
        const hasCheckboxes = Array.from(list.querySelectorAll('li')).some(li => 
          li.innerHTML.includes('type="checkbox"') || 
          li.innerHTML.includes('class="task-list-item-checkbox"')
        )
        
        if (!hasCheckboxes) return

        // Set task list attributes
        list.setAttribute('data-type', 'taskList')
        list.className = ''

        // Transform list items
        const items = list.querySelectorAll('li')
        items.forEach(item => {
          // Check if this is a task item by looking for checkbox or task list class
          const isTaskItem = item.innerHTML.includes('type="checkbox"') || 
                           item.classList.contains('task-list-item')
          
          if (!isTaskItem) return

          // Set task item attributes
          item.setAttribute('data-type', 'taskItem')
          
          // Find existing checkbox or create new one
          let checkbox = item.querySelector('input[type="checkbox"]')
          const isChecked = checkbox ? checkbox.checked : 
                          item.innerHTML.includes('checked') || 
                          item.classList.contains('is-checked')
          
          item.setAttribute('data-checked', isChecked.toString())
          item.className = ''

          // Create new structure
          const label = document.createElement('label')
          const newCheckbox = document.createElement('input')
          newCheckbox.type = 'checkbox'
          if (isChecked) {
            newCheckbox.setAttribute('checked', '')
          }

          // Get the content after the checkbox, preserving all HTML
          const content = Array.from(item.childNodes)
            .filter(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                return !node.matches('input[type="checkbox"]')
              }
              return true
            })
            .map(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                return node.outerHTML
              }
              return node.textContent
            })
            .join('')
            .replace(/^\s*\[\s?[xX]?\s?\]\s*/, '') // Remove markdown checkbox syntax
            .trim()

          // Build the new structure
          label.appendChild(newCheckbox)
          label.insertAdjacentHTML('beforeend', content)
          item.innerHTML = ''
          item.appendChild(label)
        })
      })

      return div.innerHTML
    },

    transformSuperscriptSubscript(container) {
      // Handle superscript formatting
      const strongElements = container.querySelectorAll('strong')
      strongElements.forEach(strong => {
        const text = strong.innerHTML
        // Look for ^text^ pattern
        const superscriptRegex = /\^([^^]+)\^/g
        if (superscriptRegex.test(text)) {
          // Reset regex lastIndex
          superscriptRegex.lastIndex = 0
          // Replace ^text^ with <sup>text</sup>
          strong.innerHTML = text.replace(superscriptRegex, '<sup>$1</sup>')
        }
      })

      // Handle subscript formatting
      strongElements.forEach(strong => {
        const text = strong.innerHTML
        // Look for ~text~ pattern
        const subscriptRegex = /~([^~]+)~/g
        if (subscriptRegex.test(text)) {
          // Reset regex lastIndex
          subscriptRegex.lastIndex = 0
          // Replace ~text~ with <sub>text</sub>
          strong.innerHTML = text.replace(subscriptRegex, '<sub>$1</sub>')
        }
      })

      // Also handle superscript and subscript in regular text (not in strong tags)
      const textNodes = Array.from(container.childNodes).filter(node => 
        node.nodeType === Node.TEXT_NODE || 
        (node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'P')
      )

      textNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent
          // Check for superscript
          if (text.includes('^')) {
            const span = document.createElement('span')
            span.innerHTML = text.replace(/\^([^^]+)\^/g, '<sup>$1</sup>')
            node.parentNode.replaceChild(span, node)
          }
          // Check for subscript
          if (text.includes('~')) {
            const span = document.createElement('span')
            span.innerHTML = text.replace(/~([^~]+)~/g, '<sub>$1</sub>')
            node.parentNode.replaceChild(span, node)
          }
        } else if (node.nodeName === 'P') {
          const text = node.innerHTML
          // Handle both superscript and subscript in paragraphs
          let newHtml = text
            .replace(/\^([^^]+)\^/g, '<sup>$1</sup>')
            .replace(/~([^~]+)~/g, '<sub>$1</sub>')
          if (newHtml !== text) {
            node.innerHTML = newHtml
          }
        }
      })
    },

    closeAllDropdowns() {
      this.$el.querySelectorAll('.dropdown-toggle').forEach(toggle => {
        toggle.setAttribute('aria-expanded', 'false')
      })
    }
  }
}
</script>

<style>
@import '../css/_tiptap.css';
</style> 